import { useState, useCallback } from 'react';
import {
  PageContainer,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Label,
  Alert,
  AlertDescription,
  Badge,
  useApiClient,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@startupquickstart/frontend';
import { JsonViewer } from '@/components/shared/JsonViewer';
import {
  Search,
  Trash2,
  RefreshCw,
  Database,
  Server,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Key,
  Clock,
  HardDrive,
} from 'lucide-react';

interface RedisInfo {
  enabled: boolean;
  healthy: boolean;
  message?: string;
  dbSize?: number;
  server?: {
    version: string;
    uptime: number;
    connectedClients: number;
  };
  memory?: {
    used: string;
    usedBytes: number;
    peak: string;
    peakBytes: number;
  };
  stats?: {
    totalConnections: number;
    totalCommands: number;
    keyspaceHits: number;
    keyspaceMisses: number;
  };
}

interface KeyInfo {
  key: string;
  type: string;
  ttl: number;
}

interface KeysResponse {
  keys: KeyInfo[];
  cursor: string;
  hasMore: boolean;
  count: number;
}

interface KeyDetailResponse {
  key: string;
  type: string;
  ttl: number;
  size: number;
  value: unknown;
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.length > 0 ? parts.join(' ') : '< 1m';
}

function formatTtl(ttl: number): string {
  if (ttl === -1) return 'No expiry';
  if (ttl === -2) return 'Key expired';

  const hours = Math.floor(ttl / 3600);
  const minutes = Math.floor((ttl % 3600) / 60);
  const seconds = ttl % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Redis Cache Management page for internal users
 * Allows viewing cached data, searching keys, and clearing cache
 */
export function RedisCachePage() {
  const apiClient = useApiClient();

  // Redis info state
  const [info, setInfo] = useState<RedisInfo | null>(null);
  const [infoLoading, setInfoLoading] = useState(false);
  const [infoError, setInfoError] = useState<string | null>(null);

  // Keys search state
  const [searchPattern, setSearchPattern] = useState('*');
  const [keys, setKeys] = useState<KeyInfo[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [keysError, setKeysError] = useState<string | null>(null);
  const [keysCursor, setKeysCursor] = useState('0');
  const [hasMoreKeys, setHasMoreKeys] = useState(false);

  // Key detail state
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [keyDetail, setKeyDetail] = useState<KeyDetailResponse | null>(null);
  const [keyDetailLoading, setKeyDetailLoading] = useState(false);

  // Delete state
  const [deletePattern, setDeletePattern] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteResult, setDeleteResult] = useState<{ count: number; pattern: string } | null>(null);

  // Flush state
  const [showFlushDialog, setShowFlushDialog] = useState(false);
  const [flushLoading, setFlushLoading] = useState(false);

  // Fetch Redis info
  const fetchInfo = useCallback(async () => {
    setInfoLoading(true);
    setInfoError(null);

    try {
      const response = await apiClient.get('/redis-cache/info') as { data: RedisInfo };
      setInfo(response.data);
    } catch (err) {
      setInfoError(err instanceof Error ? err.message : 'Failed to fetch Redis info');
    } finally {
      setInfoLoading(false);
    }
  }, [apiClient]);

  // Search keys
  const searchKeys = useCallback(async (reset = true) => {
    setKeysLoading(true);
    setKeysError(null);

    if (reset) {
      setKeys([]);
      setKeysCursor('0');
    }

    try {
      const params = new URLSearchParams({
        pattern: searchPattern || '*',
        limit: '50',
        details: 'true',
        cursor: reset ? '0' : keysCursor,
      });

      const response = await apiClient.get(`/redis-cache/keys?${params}`) as { data: KeysResponse };
      const data = response.data;

      setKeys(reset ? data.keys : [...keys, ...data.keys]);
      setKeysCursor(data.cursor);
      setHasMoreKeys(data.hasMore);
    } catch (err) {
      setKeysError(err instanceof Error ? err.message : 'Failed to search keys');
    } finally {
      setKeysLoading(false);
    }
  }, [apiClient, searchPattern, keysCursor, keys]);

  // Get key detail
  const fetchKeyDetail = useCallback(async (key: string) => {
    setSelectedKey(key);
    setKeyDetailLoading(true);
    setKeyDetail(null);

    try {
      const response = await apiClient.get(`/redis-cache/keys/${encodeURIComponent(key)}`) as { data: KeyDetailResponse };
      setKeyDetail(response.data);
    } catch (err) {
      console.error('Failed to fetch key detail:', err);
    } finally {
      setKeyDetailLoading(false);
    }
  }, [apiClient]);

  // Delete key
  const deleteKey = useCallback(async (key: string) => {
    try {
      await apiClient.delete(`/redis-cache/keys/${encodeURIComponent(key)}`);
      setKeys(keys.filter(k => k.key !== key));
      if (selectedKey === key) {
        setSelectedKey(null);
        setKeyDetail(null);
      }
      // Refresh info to update key count
      fetchInfo();
    } catch (err) {
      console.error('Failed to delete key:', err);
    }
  }, [apiClient, keys, selectedKey, fetchInfo]);

  // Delete by pattern
  const deleteByPattern = useCallback(async () => {
    if (!deletePattern) return;

    setDeleteLoading(true);
    setDeleteResult(null);

    try {
      const response = await apiClient.delete(`/redis-cache/keys?pattern=${encodeURIComponent(deletePattern)}`) as { data: { deletedCount: number } };
      setDeleteResult({ count: response.data.deletedCount, pattern: deletePattern });
      setDeletePattern('');
      // Refresh keys and info
      searchKeys(true);
      fetchInfo();
    } catch (err) {
      console.error('Failed to delete by pattern:', err);
    } finally {
      setDeleteLoading(false);
    }
  }, [apiClient, deletePattern, searchKeys, fetchInfo]);

  // Flush all
  const flushAll = useCallback(async () => {
    setFlushLoading(true);

    try {
      await apiClient.post('/redis-cache/flush', { confirm: true });
      setShowFlushDialog(false);
      setKeys([]);
      setSelectedKey(null);
      setKeyDetail(null);
      fetchInfo();
    } catch (err) {
      console.error('Failed to flush database:', err);
    } finally {
      setFlushLoading(false);
    }
  }, [apiClient, fetchInfo]);

  // Calculate hit rate
  const hitRate = info?.stats
    ? ((info.stats.keyspaceHits / (info.stats.keyspaceHits + info.stats.keyspaceMisses)) * 100).toFixed(1)
    : '0';

  return (
    <PageContainer>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Redis Cache</h1>
            <p className="text-muted-foreground mt-2">
              Manage and monitor the Redis cache for the mobile API
            </p>
          </div>
          <Button onClick={fetchInfo} disabled={infoLoading} variant="outline">
            {infoLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-2">Refresh Status</span>
          </Button>
        </div>

        {/* Status Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-muted p-3">
                  <Server className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  {info ? (
                    <div className="flex items-center gap-2">
                      {info.healthy ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                      <span className="font-medium">
                        {info.enabled ? (info.healthy ? 'Connected' : 'Unhealthy') : 'Disabled'}
                      </span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Click refresh</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-muted p-3">
                  <Key className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Keys</p>
                  <p className="text-2xl font-semibold">{info?.dbSize?.toLocaleString() ?? '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-muted p-3">
                  <HardDrive className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Memory Used</p>
                  <p className="text-2xl font-semibold">{info?.memory?.used ?? '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <div className="rounded-lg bg-muted p-3">
                  <Database className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Hit Rate</p>
                  <p className="text-2xl font-semibold">{info?.stats ? `${hitRate}%` : '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {infoError && (
          <Alert variant="destructive">
            <AlertDescription>{infoError}</AlertDescription>
          </Alert>
        )}

        {/* Server Details */}
        {info?.server && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Server Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                <div>
                  <p className="text-sm text-muted-foreground">Redis Version</p>
                  <p className="font-medium">{info.server.version}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Uptime</p>
                  <p className="font-medium">{formatUptime(info.server.uptime)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Connected Clients</p>
                  <p className="font-medium">{info.server.connectedClients}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Peak Memory</p>
                  <p className="font-medium">{info.memory?.peak}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search Keys */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Search Keys</CardTitle>
            <CardDescription>
              Search for cached keys using glob patterns (e.g., mobile:attractions:*, *:en:*)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="searchPattern" className="sr-only">Search Pattern</Label>
                <Input
                  id="searchPattern"
                  value={searchPattern}
                  onChange={(e) => setSearchPattern(e.target.value)}
                  placeholder="Enter pattern (e.g., mobile:* or *)"
                  onKeyDown={(e) => e.key === 'Enter' && searchKeys(true)}
                />
              </div>
              <Button onClick={() => searchKeys(true)} disabled={keysLoading}>
                {keysLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
                <span className="ml-2">Search</span>
              </Button>
            </div>

            {keysError && (
              <Alert variant="destructive">
                <AlertDescription>{keysError}</AlertDescription>
              </Alert>
            )}

            {keys.length > 0 && (
              <div className="border rounded-lg divide-y max-h-96 overflow-auto">
                {keys.map((keyInfo) => (
                  <div
                    key={keyInfo.key}
                    className={`flex items-center justify-between p-3 hover:bg-muted/50 cursor-pointer ${
                      selectedKey === keyInfo.key ? 'bg-muted' : ''
                    }`}
                    onClick={() => fetchKeyDetail(keyInfo.key)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-sm truncate">{keyInfo.key}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{keyInfo.type}</Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatTtl(keyInfo.ttl)}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        deleteKey(keyInfo.key);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {hasMoreKeys && (
              <Button
                variant="outline"
                onClick={() => searchKeys(false)}
                disabled={keysLoading}
                className="w-full"
              >
                {keysLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Load More
              </Button>
            )}

            {keys.length === 0 && !keysLoading && !keysError && (
              <p className="text-center text-muted-foreground py-8">
                No keys found. Click Search to find cached keys.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Key Detail */}
        {selectedKey && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Key Detail</CardTitle>
                  <CardDescription className="font-mono break-all">{selectedKey}</CardDescription>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteKey(selectedKey)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Key
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {keyDetailLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : keyDetail ? (
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <Badge variant="outline">{keyDetail.type}</Badge>
                    <span className="text-sm text-muted-foreground">
                      TTL: {formatTtl(keyDetail.ttl)}
                    </span>
                    {keyDetail.size > 0 && (
                      <span className="text-sm text-muted-foreground">
                        Size: {(keyDetail.size / 1024).toFixed(2)} KB
                      </span>
                    )}
                  </div>
                  <div className="border rounded-lg p-4 bg-muted/30">
                    <JsonViewer data={keyDetail.value} />
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        )}

        {/* Delete by Pattern */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Delete by Pattern</CardTitle>
            <CardDescription>
              Delete multiple keys matching a pattern. Use with caution.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="deletePattern" className="sr-only">Delete Pattern</Label>
                <Input
                  id="deletePattern"
                  value={deletePattern}
                  onChange={(e) => setDeletePattern(e.target.value)}
                  placeholder="e.g., mobile:attractions:*"
                />
              </div>
              <Button
                variant="destructive"
                onClick={deleteByPattern}
                disabled={!deletePattern || deleteLoading}
              >
                {deleteLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span className="ml-2">Delete Matching</span>
              </Button>
            </div>

            {deleteResult && (
              <Alert>
                <AlertDescription>
                  Deleted {deleteResult.count} keys matching pattern "{deleteResult.pattern}"
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-lg text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>
              Destructive actions that cannot be undone.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 border border-destructive/30 rounded-lg">
              <div>
                <p className="font-medium">Flush All Keys</p>
                <p className="text-sm text-muted-foreground">
                  Delete all cached data. This will affect all users until the cache is rebuilt.
                </p>
              </div>
              <Button variant="destructive" onClick={() => setShowFlushDialog(true)}>
                Flush Database
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Flush Confirmation Dialog */}
        <Dialog open={showFlushDialog} onOpenChange={setShowFlushDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Flush Redis Database</DialogTitle>
              <DialogDescription>
                This will delete all {info?.dbSize?.toLocaleString() ?? 'cached'} keys from the database.
                The cache will be rebuilt automatically as users access the API, but there may be
                temporary performance degradation.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setShowFlushDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={flushAll} disabled={flushLoading}>
                {flushLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Yes, Flush All Keys
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageContainer>
  );
}
